// src/pages/WerkbonnenOverzicht.js
// vWBO-006 - Open PDF gebruikt lokaal de backend (poort 3001); werkbonnenlijst ververst ook bij terugkeer naar het tabblad.
// vWBO-005
// - Bovenste zoekregel en knoppen gelijkgetrokken met Organisaties.js
// - Zoek, Aantal, Status, Vanaf, Tot en met, Spreek en Vernieuw op één regel
// - Geen aparte Zoeken-knop meer
// - Tweede regel met Niet opgeleverd, Ter akkoord, Geaccordeerd en Opgeleverd
// - Kopregels uitgebreid met sortering en kolomfilters zoals Organisaties.js

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useVoiceCommands from "../components/useVoiceCommands";
import HelpButton from "../components/HelpButton";

const VERSION = "vWBO-006";
const EMPTY_VAL = "__EMPTY__";

function getParam(name) {
  try {
    const url = new URL(window.location.href);
    return url.searchParams.get(name) || "";
  } catch {
    return "";
  }
}

function safeText(value, fallback = "") {
  if (value === null || value === undefined) return fallback;
  return String(value);
}

function safeTrim(value) {
  return safeText(value).trim();
}

function norm(value) {
  return safeText(value)
    .toLowerCase()
    .trim()
    .replace(/[，、]/g, ",")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[;:!?]/g, "")
    .replace(/\s+/g, " ");
}

function uniqSorted(values) {
  return Array.from(
    new Set((values || []).map((value) => safeTrim(value)).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, "nl"));
}

function isoDate(value) {
  const text = safeText(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : "";
}

function nlDate(value) {
  const date = isoDate(value);
  if (!date) return "";
  const [year, month, day] = date.split("-");
  return `${day}-${month}-${year}`;
}

function nlDateTime(value) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return safeText(value);

  try {
    return date.toLocaleString("nl-NL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return safeText(value);
  }
}

function buildHeaders() {
  let user = {};

  try {
    user = JSON.parse(
      sessionStorage.getItem("user") ||
      localStorage.getItem("user") ||
      "{}"
    );
  } catch {}

  const headers = {
    "Content-Type": "application/json",
    "x-tenant":
      sessionStorage.getItem("tenant") ||
      localStorage.getItem("tenant") ||
      "demo",
    "x-role": user?.role || "user",
    "x-staffiq-user-id": user?.id || "",
    "x-staffiq-user-email": user?.email || "",
    "x-staffiq-user-name": user?.display_name || user?.name || "",
    "x-company-code-ids":
      sessionStorage.getItem("staffiq_company_code_ids") ||
      localStorage.getItem("staffiq_company_code_ids") ||
      "",
  };

  const token =
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    "";

  if (token) headers.Authorization = `Bearer ${token}`;

  Object.keys(headers).forEach((key) => {
    if (!headers[key]) delete headers[key];
  });

  return headers;
}

async function fetchJson(url) {
  const response = await fetch(url, {
    headers: buildHeaders(),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data?.error || `${response.status} ${response.statusText}`);
  }

  return data;
}

function completionLabel(value) {
  const status = safeText(value).trim().toUpperCase();

  if (status === "COMPLETED") return "Opgeleverd";
  if (status === "PENDING_APPROVAL") return "Ter akkoord";
  return "Niet opgeleverd";
}

function approvalLabel(value) {
  const status = safeText(value).trim().toLowerCase();

  if (status === "approved") return "Geaccordeerd";
  if (status === "rejected") return "Niet akkoord";
  if (status === "viewed") return "Bekeken";
  if (status === "sent") return "Verstuurd";
  if (status === "expired") return "Verlopen";
  if (status === "concept") return "Concept";
  return "";
}

function processStatusKey(item) {
  const completion = safeText(item?.completion_status).trim().toUpperCase();
  const approval = safeText(item?.approval_status).trim().toLowerCase();

  if (completion === "COMPLETED") return "COMPLETED";
  if (approval === "approved") return "APPROVED";
  if (completion === "PENDING_APPROVAL") return "PENDING_APPROVAL";
  return "NOT_COMPLETED";
}

function processStatusLabel(item) {
  const key = processStatusKey(item);

  if (key === "COMPLETED") return "Opgeleverd";
  if (key === "APPROVED") return "Geaccordeerd";
  if (key === "PENDING_APPROVAL") return "Ter akkoord";
  return "Niet opgeleverd";
}

function statusStyle(value) {
  const key = safeText(value).trim().toUpperCase();

  if (key === "COMPLETED") {
    return { background: "#e8f5e9", color: "#1b5e20", border: "#a5d6a7" };
  }

  if (key === "APPROVED") {
    return { background: "#eaf2f8", color: "#1f4e79", border: "#b8d2e8" };
  }

  if (key === "PENDING_APPROVAL") {
    return { background: "#fff8e1", color: "#8a5a00", border: "#ffe082" };
  }

  return { background: "#f3f4f6", color: "#4b5563", border: "#d1d5db" };
}

export default function WerkbonnenOverzicht() {
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const pageTopRef = useRef(null);
  const midScrollRef = useRef(null);
  const rowRefs = useRef([]);

  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [listIndex, setListIndex] = useState(-1);

  const [q, setQ] = useState("");
  const [organisationFilterId, setOrganisationFilterId] = useState(getParam("organisation_id"));
  const [topStatus, setTopStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [offerFilter, setOfferFilter] = useState("");
  const [relationFilter, setRelationFilter] = useState("");
  const [executionFilter, setExecutionFilter] = useState("");
  const [executedByFilter, setExecutedByFilter] = useState("");
  const [workOrderStatusFilter, setWorkOrderStatusFilter] = useState("");
  const [approvalStatusFilter, setApprovalStatusFilter] = useState("");
  const [approvalDateFilter, setApprovalDateFilter] = useState("");
  const [approvalNameFilter, setApprovalNameFilter] = useState("");
  const [companyCodeFilter, setCompanyCodeFilter] = useState("");
  const [documentFilter, setDocumentFilter] = useState("");

  const [sort, setSort] = useState({
    key: "execution_date",
    dir: "desc",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");
  const [speechInfo, setSpeechInfo] = useState("");
  const [hideVoiceError, setHideVoiceError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  async function loadItems() {
    try {
      setLoading(true);
      setError("");

      const data = await fetchJson("/api/work-order-overview");
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setItems([]);
      setError(err?.message || "Werkbonnenoverzicht laden mislukt.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  // vWBO-006: opnieuw ophalen als een eerdere werkbon in een ander tabblad is opgeslagen.
  // Zonder deze verversing kan het PDF-icoon nog uitgeschakeld lijken.
  useEffect(() => {
    function refreshOnReturn() {
      if (document.visibilityState === "visible") loadItems();
    }

    window.addEventListener("focus", refreshOnReturn);
    return () => window.removeEventListener("focus", refreshOnReturn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function handleScopeChange() {
      setReloadKey((value) => value + 1);
    }

    window.addEventListener(
      "staffiq-company-scope-changed",
      handleScopeChange
    );

    return () => {
      window.removeEventListener(
        "staffiq-company-scope-changed",
        handleScopeChange
      );
    };
  }, []);

  const filterOptions = useMemo(() => {
    return {
      offer: uniqSorted(items.map((item) => item.offer_number || item.offer_id)),
      relation: uniqSorted(items.map((item) => item.organisation_name)),
      execution: uniqSorted(items.map((item) => isoDate(item.execution_date))),
      executedBy: uniqSorted(items.map((item) => item.executed_by)),
      workOrderStatus: uniqSorted(items.map((item) => completionLabel(item.completion_status))),
      approvalStatus: uniqSorted(items.map((item) => approvalLabel(item.approval_status))),
      approvalDate: uniqSorted(items.map((item) => isoDate(item.approval_approved_at))),
      approvalName: uniqSorted(items.map((item) => item.approval_name || item.approved_name)),
      companyCode: uniqSorted(items.map((item) => item.company_code_name || item.company_code)),
      document: uniqSorted(items.map((item) => item.document_id ? "PDF" : "")),
    };
  }, [items]);

  const filteredItems = useMemo(() => {
    const query = norm(q);

    function matches(value, filter) {
      const clean = safeTrim(value);

      if (!filter) return true;
      if (filter === EMPTY_VAL) return !clean;

      return clean === filter;
    }

    return items.filter((item) => {
      const executionDate = isoDate(item.execution_date);

      if (
        organisationFilterId &&
        String(item.organisation_id || "") !== String(organisationFilterId)
      ) {
        return false;
      }
      const approvalDate = isoDate(item.approval_approved_at);
      const processKey = processStatusKey(item);

      if (query) {
        const haystack = norm([
          item.offer_number,
          item.offer_id,
          item.organisation_name,
          item.contact_name,
          item.executed_by,
          item.approval_name,
          item.approved_name,
          item.company_code_name,
          item.company_code,
          completionLabel(item.completion_status),
          approvalLabel(item.approval_status),
        ].join(" "));

        if (!haystack.includes(query)) return false;
      }

      if (topStatus && processKey !== topStatus) return false;
      if (dateFrom && (!executionDate || executionDate < dateFrom)) return false;
      if (dateTo && (!executionDate || executionDate > dateTo)) return false;

      if (!matches(item.offer_number || item.offer_id, offerFilter)) return false;
      if (!matches(item.organisation_name, relationFilter)) return false;
      if (!matches(executionDate, executionFilter)) return false;
      if (!matches(item.executed_by, executedByFilter)) return false;
      if (!matches(completionLabel(item.completion_status), workOrderStatusFilter)) return false;
      if (!matches(approvalLabel(item.approval_status), approvalStatusFilter)) return false;
      if (!matches(approvalDate, approvalDateFilter)) return false;
      if (!matches(item.approval_name || item.approved_name, approvalNameFilter)) return false;
      if (!matches(item.company_code_name || item.company_code, companyCodeFilter)) return false;
      if (!matches(item.document_id ? "PDF" : "", documentFilter)) return false;

      return true;
    });
  }, [
    items,
    q,
    topStatus,
    dateFrom,
    dateTo,
    offerFilter,
    relationFilter,
    executionFilter,
    executedByFilter,
    workOrderStatusFilter,
    approvalStatusFilter,
    approvalDateFilter,
    approvalNameFilter,
    companyCodeFilter,
    documentFilter,
    organisationFilterId,
  ]);

  const sortedItems = useMemo(() => {
    const rows = [...filteredItems];
    const direction = sort.dir === "desc" ? -1 : 1;

    rows.sort((a, b) => {
      let av = "";
      let bv = "";

      if (sort.key === "offer_number") {
        av = safeText(a.offer_number || a.offer_id).toLowerCase();
        bv = safeText(b.offer_number || b.offer_id).toLowerCase();
      } else if (sort.key === "organisation_name") {
        av = safeText(a.organisation_name).toLowerCase();
        bv = safeText(b.organisation_name).toLowerCase();
      } else if (sort.key === "execution_date") {
        av = isoDate(a.execution_date);
        bv = isoDate(b.execution_date);
      } else if (sort.key === "executed_by") {
        av = safeText(a.executed_by).toLowerCase();
        bv = safeText(b.executed_by).toLowerCase();
      } else if (sort.key === "completion_status") {
        av = completionLabel(a.completion_status).toLowerCase();
        bv = completionLabel(b.completion_status).toLowerCase();
      } else if (sort.key === "approval_status") {
        av = approvalLabel(a.approval_status).toLowerCase();
        bv = approvalLabel(b.approval_status).toLowerCase();
      } else if (sort.key === "approval_approved_at") {
        av = isoDate(a.approval_approved_at);
        bv = isoDate(b.approval_approved_at);
      } else if (sort.key === "approval_name") {
        av = safeText(a.approval_name || a.approved_name).toLowerCase();
        bv = safeText(b.approval_name || b.approved_name).toLowerCase();
      } else if (sort.key === "company_code_name") {
        av = safeText(a.company_code_name || a.company_code).toLowerCase();
        bv = safeText(b.company_code_name || b.company_code).toLowerCase();
      } else if (sort.key === "document") {
        av = a.document_id ? "1" : "0";
        bv = b.document_id ? "1" : "0";
      }

      if (av === bv) return 0;
      return av < bv ? -1 * direction : 1 * direction;
    });

    return rows;
  }, [filteredItems, sort]);

  const selected =
    items.find((item) => String(item.id) === String(selectedId)) || null;

  const counts = useMemo(() => {
    return {
      notCompleted: items.filter(
        (item) => processStatusKey(item) === "NOT_COMPLETED"
      ).length,
      pending: items.filter(
        (item) => processStatusKey(item) === "PENDING_APPROVAL"
      ).length,
      approved: items.filter(
        (item) => processStatusKey(item) === "APPROVED"
      ).length,
      completed: items.filter(
        (item) => processStatusKey(item) === "COMPLETED"
      ).length,
    };
  }, [items]);

  const gridCount = Math.min(sortedItems.length, 9999);

  function sortToggle(key) {
    setSort((previous) => {
      if (previous.key !== key) return { key, dir: "asc" };
      return {
        key,
        dir: previous.dir === "asc" ? "desc" : "asc",
      };
    });
  }

  function sortArrow(key) {
    if (sort.key !== key) return "";
    return sort.dir === "asc" ? " ▲" : " ▼";
  }

  function resetColumnFilters() {
    setOfferFilter("");
    setRelationFilter("");
    setExecutionFilter("");
    setExecutedByFilter("");
    setWorkOrderStatusFilter("");
    setApprovalStatusFilter("");
    setApprovalDateFilter("");
    setApprovalNameFilter("");
    setCompanyCodeFilter("");
    setDocumentFilter("");
  }

  function resetFilters() {
    setQ("");
    setOrganisationFilterId("");
    setTopStatus("");
    setDateFrom("");
    setDateTo("");
    resetColumnFilters();
    setSelectedId("");
    setListIndex(-1);
    setSort({ key: "execution_date", dir: "desc" });
    setHideVoiceError(true);
    setReloadKey((value) => value + 1);

    setFlash("Vernieuwd ✅");
    setTimeout(() => setFlash(""), 800);

    requestAnimationFrame(() => {
      try {
        midScrollRef.current?.scrollTo?.({
          top: 0,
          left: 0,
          behavior: "auto",
        });
      } catch {}

      try {
        pageTopRef.current?.scrollIntoView?.({
          behavior: "auto",
          block: "start",
        });
      } catch {}

      if (window.innerWidth > 768) {
        try {
          searchRef.current?.focus?.({ preventScroll: true });
          searchRef.current?.select?.();
        } catch {
          searchRef.current?.focus?.();
        }
      }
    });
  }

  function openOrder(row) {
    if (!row?.offer_id) return;

    try {
      sessionStorage.setItem(
        "staffiq_reopen_offer_id",
        String(row.offer_id)
      );
    } catch {}

    navigate("/offertes");
  }

  function openWorkOrder(row) {
    if (!row?.offer_id) return;
    navigate(`/werkbon-oplevering/${row.offer_id}`);
  }

  function openDocument(row) {
    if (!row?.document_id) return;

    const documentPath = `/api/documents/${encodeURIComponent(row.document_id)}/open`;
    // Op localhost:3000 kan een rechtstreeks geopend /api-adres door de React-
    // ontwikkelserver als pagina worden behandeld, waarna het inlogscherm verschijnt.
    // Een PDF moet daar rechtstreeks vanaf de backend (:3001) worden opgehaald.
    // Op server 1/2 blijft het bestaande Nginx-adres (zelfde domein) behouden.
    const localFrontend =
      (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1") &&
      window.location.port === "3000";
    const documentUrl = localFrontend
      ? `http://${window.location.hostname}:3001${documentPath}`
      : documentPath;

    window.open(documentUrl, "_blank", "noopener,noreferrer");
  }

  function moveCursor(delta) {
    setListIndex((previous) => {
      if (!sortedItems.length) return -1;

      const current = previous < 0 ? 0 : previous;
      const next = Math.max(
        0,
        Math.min(sortedItems.length - 1, current + delta)
      );

      setTimeout(() => {
        rowRefs.current?.[next]?.scrollIntoView?.({
          block: "nearest",
        });
      }, 0);

      return next;
    });
  }

  function openCurrentRow() {
    if (!sortedItems.length) return;

    const index =
      listIndex >= 0 && listIndex < sortedItems.length
        ? listIndex
        : 0;

    const row = sortedItems[index];
    if (!row) return;

    setListIndex(index);
    setSelectedId(String(row.id));
  }

  function handleVoiceCommand(raw) {
    const spoken = norm(raw);

    if (spoken === "volgende") {
      moveCursor(1);
      return true;
    }

    if (spoken === "vorige") {
      moveCursor(-1);
      return true;
    }

    if (
      spoken === "selecteer" ||
      spoken === "selecteren" ||
      spoken === "rechts"
    ) {
      openCurrentRow();
      return true;
    }

    if (spoken === "vernieuw" || spoken === "vernieuwen") {
      resetFilters();
      return true;
    }

    if (spoken === "niet opgeleverd") {
      setTopStatus("NOT_COMPLETED");
      return true;
    }

    if (spoken === "ter akkoord") {
      setTopStatus("PENDING_APPROVAL");
      return true;
    }

    if (spoken === "geaccordeerd" || spoken === "akkoord") {
      setTopStatus("APPROVED");
      return true;
    }

    if (spoken === "opgeleverd") {
      setTopStatus("COMPLETED");
      return true;
    }

    if (spoken === "alles" || spoken === "alle statussen") {
      setTopStatus("");
      return true;
    }

    return false;
  }

  const voice = useVoiceCommands({
    lang: "nl-NL",
    timeoutMs: 6500,

    onCommand: (_command, rawHeard) => {
      const raw = safeTrim(rawHeard);

      if (raw) {
        setSpeechInfo(`🎙 ${raw}`);
        setTimeout(() => setSpeechInfo(""), 2000);
      }

      return raw ? handleVoiceCommand(raw) : false;
    },

    onDictation: (spokenText) => {
      const raw = safeTrim(spokenText);
      if (!raw) return;

      setSpeechInfo(`🎤 ${raw}`);
      setTimeout(() => setSpeechInfo(""), 2000);

      if (handleVoiceCommand(raw)) return;

      setQ(raw);
      setListIndex(0);
    },
  });

  function startSpeak() {
    if (!voice.supported || voice.listening) return;
    voice.startOnce();
  }

  const isMobile = window.innerWidth <= 768;
  const TABLE_MIN_WIDTH = 1240;

  const searchInputStyle = {
    padding: "7px 10px",
    width: 180,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    outline: "none",
    boxSizing: "border-box",
  };

  const topSelectStyle = {
    height: 32,
    padding: "0 8px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    boxSizing: "border-box",
  };

  const topDateStyle = {
    height: 32,
    width: 138,
    padding: "0 8px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    boxSizing: "border-box",
  };

  const buttonStyle = {
    padding: "7px 12px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  const buttonDisabledStyle = {
    ...buttonStyle,
    opacity: 0.55,
    cursor: "not-allowed",
  };

  const countStyle = {
    minWidth: 52,
    height: 32,
    padding: "0 10px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "#f7fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
    color: "#1f4e79",
    boxSizing: "border-box",
    whiteSpace: "nowrap",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    tableLayout: "fixed",
  };

  const thBase = {
    textAlign: "left",
    fontSize: 12,
    color: "#444",
    fontWeight: 700,
    padding: "8px 6px",
    borderBottom: "1px solid #e6e6e6",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 5,
    background: "#fff",
    userSelect: "none",
  };

  const thClickable = {
    ...thBase,
    cursor: "pointer",
  };

  const filterThStyle = {
    ...thBase,
    fontWeight: 400,
    padding: "6px 6px 8px 6px",
    borderBottom: "1px solid #ededed",
    top: 32,
    zIndex: 4,
    cursor: "default",
  };

  const selectStyle = {
    height: 30,
    padding: "0 8px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    width: "100%",
    boxSizing: "border-box",
  };

  const tdStyle = {
    padding: "7px 6px",
    borderBottom: "1px solid #efefef",
    fontSize: 13,
    color: "#222",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    verticalAlign: "middle",
  };

  const colWidths = {
    offer: 105,
    relation: 150,
    execution: 120,
    executedBy: 125,
    workOrderStatus: 120,
    approvalStatus: 115,
    approvalDate: 140,
    approvalName: 120,
    companyCode: 110,
    document: 75,
  };

  function StatusButton({ statusKey, label, count }) {
    const active = topStatus === statusKey;
    const badge = statusStyle(statusKey);

    return (
      <button
        type="button"
        onClick={() =>
          setTopStatus((previous) =>
            previous === statusKey ? "" : statusKey
          )
        }
        style={{
          ...buttonStyle,
          background: active ? badge.background : "white",
          color: active ? badge.color : "#333",
          border: `1px solid ${active ? badge.border : "#d9d9d9"}`,
          fontWeight: active ? 700 : 400,
        }}
      >
        {label}: <b>{count}</b>
      </button>
    );
  }

  function FilterSelect({
    value,
    onChange,
    options,
    hasEmpty = false,
    formatOption = (option) => option,
  }) {
    return (
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={selectStyle}
      >
        <option value="">(alle)</option>
        {hasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
        {options.map((option) => (
          <option key={option} value={option}>
            {formatOption(option)}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div style={{ width: "100%", minWidth: 0 }}>
      <div
        ref={pageTopRef}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginTop: 0,
          marginBottom: 10,
        }}
      >
        <h1 style={{ margin: 0 }}>
          Werkbonnen overzicht{" "}
          <span style={{ fontSize: 12, opacity: 0.5 }}>{VERSION}</span>
        </h1>

        <HelpButton
          topic="werkbonnen-overzicht"
          title="Help over Werkbonnen overzicht"
        />
      </div>

      {!isMobile ? (
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "nowrap",
          }}
        >
          <input
            ref={searchRef}
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="Zoek…"
            style={searchInputStyle}
          />

          <div
            title={`Aantal regels in het grid: ${gridCount}`}
            style={countStyle}
          >
            {gridCount}
          </div>

          <select
            value={topStatus}
            onChange={(event) => setTopStatus(event.target.value)}
            style={{ ...topSelectStyle, width: 145 }}
            title="Status"
          >
            <option value="">Alle statussen</option>
            <option value="NOT_COMPLETED">Niet opgeleverd</option>
            <option value="PENDING_APPROVAL">Ter akkoord</option>
            <option value="APPROVED">Geaccordeerd</option>
            <option value="COMPLETED">Opgeleverd</option>
          </select>

          <input
            type="date"
            value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
            style={topDateStyle}
            title="Vanaf"
          />

          <input
            type="date"
            value={dateTo}
            onChange={(event) => setDateTo(event.target.value)}
            style={topDateStyle}
            title="Tot en met"
          />

          <button
            onMouseDown={(event) => event.preventDefault()}
            onTouchStart={(event) => event.preventDefault()}
            onClick={startSpeak}
            disabled={!voice.supported || voice.listening}
            style={
              !voice.supported || voice.listening
                ? buttonDisabledStyle
                : buttonStyle
            }
          >
            {voice.listening ? "🎤 Luistert…" : "🎤 Spreek"}
          </button>

          <button type="button" onClick={resetFilters} style={buttonStyle}>
            🔄 Vernieuw
          </button>
        </div>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) 58px",
              gap: 6,
              marginBottom: 8,
            }}
          >
            <input
              ref={searchRef}
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder="Zoek…"
              style={{
                ...searchInputStyle,
                width: "100%",
                minWidth: 0,
              }}
            />

            <div style={{ ...countStyle, width: "100%", minWidth: 0 }}>
              {gridCount}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 6,
              marginBottom: 6,
            }}
          >
            <select
              value={topStatus}
              onChange={(event) => setTopStatus(event.target.value)}
              style={{ ...topSelectStyle, width: "100%" }}
            >
              <option value="">Alle statussen</option>
              <option value="NOT_COMPLETED">Niet opgeleverd</option>
              <option value="PENDING_APPROVAL">Ter akkoord</option>
              <option value="APPROVED">Geaccordeerd</option>
              <option value="COMPLETED">Opgeleverd</option>
            </select>

            <button
              onClick={startSpeak}
              disabled={!voice.supported || voice.listening}
              style={
                !voice.supported || voice.listening
                  ? buttonDisabledStyle
                  : buttonStyle
              }
            >
              {voice.listening ? "🎤..." : "🎤 Spreek"}
            </button>

            <input
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              style={{ ...topDateStyle, width: "100%" }}
              title="Vanaf"
            />

            <input
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
              style={{ ...topDateStyle, width: "100%" }}
              title="Tot en met"
            />

            <button
              type="button"
              onClick={resetFilters}
              style={{ ...buttonStyle, gridColumn: "1 / -1" }}
            >
              🔄 Vernieuw
            </button>
          </div>
        </>
      )}

      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          marginTop: 10,
          marginBottom: 10,
        }}
      >
        <StatusButton
          statusKey="NOT_COMPLETED"
          label="Niet opgeleverd"
          count={counts.notCompleted}
        />

        <StatusButton
          statusKey="PENDING_APPROVAL"
          label="Ter akkoord"
          count={counts.pending}
        />

        <StatusButton
          statusKey="APPROVED"
          label="Geaccordeerd"
          count={counts.approved}
        />

        <StatusButton
          statusKey="COMPLETED"
          label="Opgeleverd"
          count={counts.completed}
        />
      </div>

      <div style={{ minHeight: 20 }}>
        {loading && <div>Laden…</div>}
        {error && <div style={{ color: "crimson" }}>{error}</div>}
        {flash && <div style={{ color: "green" }}>{flash}</div>}
        {speechInfo && <div style={{ color: "#1f4e79" }}>{speechInfo}</div>}
        {voice.error && !hideVoiceError && (
          <div style={{ color: "crimson" }}>
            Spraakfout: {voice.error}
          </div>
        )}
      </div>

      {!loading && !error && (
        <div style={{ marginTop: 4, minWidth: 0 }}>
          <div
            ref={midScrollRef}
            style={{
              overflowX: "scroll",
              overflowY: "auto",
              maxHeight: "calc(100vh - 300px)",
              border: "1px solid #f0f0f0",
              borderRadius: 6,
              background: "#fff",
            }}
          >
            <div style={{ minWidth: TABLE_MIN_WIDTH }}>
              <table style={tableStyle}>
                <colgroup>
                  <col style={{ width: colWidths.offer }} />
                  <col style={{ width: colWidths.relation }} />
                  <col style={{ width: colWidths.execution }} />
                  <col style={{ width: colWidths.executedBy }} />
                  <col style={{ width: colWidths.workOrderStatus }} />
                  <col style={{ width: colWidths.approvalStatus }} />
                  <col style={{ width: colWidths.approvalDate }} />
                  <col style={{ width: colWidths.approvalName }} />
                  <col style={{ width: colWidths.companyCode }} />
                  <col style={{ width: colWidths.document }} />
                </colgroup>

                <thead>
                  <tr>
                    <th style={thClickable} onClick={() => sortToggle("offer_number")}>
                      Opdracht{sortArrow("offer_number")}
                    </th>

                    <th style={thClickable} onClick={() => sortToggle("organisation_name")}>
                      Relatie{sortArrow("organisation_name")}
                    </th>

                    <th style={thClickable} onClick={() => sortToggle("execution_date")}>
                      Uitvoering{sortArrow("execution_date")}
                    </th>

                    <th style={thClickable} onClick={() => sortToggle("executed_by")}>
                      Uitgevoerd door{sortArrow("executed_by")}
                    </th>

                    <th style={thClickable} onClick={() => sortToggle("completion_status")}>
                      Werkbonstatus{sortArrow("completion_status")}
                    </th>

                    <th style={thClickable} onClick={() => sortToggle("approval_status")}>
                      Akkoordstatus{sortArrow("approval_status")}
                    </th>

                    <th style={thClickable} onClick={() => sortToggle("approval_approved_at")}>
                      Akkoorddatum{sortArrow("approval_approved_at")}
                    </th>

                    <th style={thClickable} onClick={() => sortToggle("approval_name")}>
                      Akkoordgever{sortArrow("approval_name")}
                    </th>

                    <th style={thClickable} onClick={() => sortToggle("company_code_name")}>
                      Bedrijfscode{sortArrow("company_code_name")}
                    </th>

                    <th style={thClickable} onClick={() => sortToggle("document")}>
                      Document{sortArrow("document")}
                    </th>
                  </tr>

                  <tr>
                    <th style={filterThStyle}>
                      <FilterSelect
                        value={offerFilter}
                        onChange={setOfferFilter}
                        options={filterOptions.offer}
                        hasEmpty={items.some(
                          (item) => !safeTrim(item.offer_number || item.offer_id)
                        )}
                      />
                    </th>

                    <th style={filterThStyle}>
                      <FilterSelect
                        value={relationFilter}
                        onChange={setRelationFilter}
                        options={filterOptions.relation}
                        hasEmpty={items.some(
                          (item) => !safeTrim(item.organisation_name)
                        )}
                      />
                    </th>

                    <th style={filterThStyle}>
                      <FilterSelect
                        value={executionFilter}
                        onChange={setExecutionFilter}
                        options={filterOptions.execution}
                        hasEmpty={items.some(
                          (item) => !isoDate(item.execution_date)
                        )}
                        formatOption={nlDate}
                      />
                    </th>

                    <th style={filterThStyle}>
                      <FilterSelect
                        value={executedByFilter}
                        onChange={setExecutedByFilter}
                        options={filterOptions.executedBy}
                        hasEmpty={items.some(
                          (item) => !safeTrim(item.executed_by)
                        )}
                      />
                    </th>

                    <th style={filterThStyle}>
                      <FilterSelect
                        value={workOrderStatusFilter}
                        onChange={setWorkOrderStatusFilter}
                        options={filterOptions.workOrderStatus}
                      />
                    </th>

                    <th style={filterThStyle}>
                      <FilterSelect
                        value={approvalStatusFilter}
                        onChange={setApprovalStatusFilter}
                        options={filterOptions.approvalStatus}
                        hasEmpty={items.some(
                          (item) => !safeTrim(approvalLabel(item.approval_status))
                        )}
                      />
                    </th>

                    <th style={filterThStyle}>
                      <FilterSelect
                        value={approvalDateFilter}
                        onChange={setApprovalDateFilter}
                        options={filterOptions.approvalDate}
                        hasEmpty={items.some(
                          (item) => !isoDate(item.approval_approved_at)
                        )}
                        formatOption={nlDate}
                      />
                    </th>

                    <th style={filterThStyle}>
                      <FilterSelect
                        value={approvalNameFilter}
                        onChange={setApprovalNameFilter}
                        options={filterOptions.approvalName}
                        hasEmpty={items.some(
                          (item) =>
                            !safeTrim(item.approval_name || item.approved_name)
                        )}
                      />
                    </th>

                    <th style={filterThStyle}>
                      <FilterSelect
                        value={companyCodeFilter}
                        onChange={setCompanyCodeFilter}
                        options={filterOptions.companyCode}
                        hasEmpty={items.some(
                          (item) =>
                            !safeTrim(item.company_code_name || item.company_code)
                        )}
                      />
                    </th>

                    <th style={filterThStyle}>
                      <FilterSelect
                        value={documentFilter}
                        onChange={setDocumentFilter}
                        options={filterOptions.document}
                        hasEmpty={items.some((item) => !item.document_id)}
                      />
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {sortedItems.map((item, index) => {
                    const isSelected =
                      String(item.id) === String(selectedId);
                    const isCursor = index === listIndex;
                    const processKey = processStatusKey(item);
                    const badge = statusStyle(processKey);

                    let background = "transparent";
                    if (isSelected) background = "#e6f0ff";
                    else if (isCursor) background = "#f3f7ff";

                    return (
                      <tr
                        key={item.id}
                        ref={(element) => {
                          rowRefs.current[index] = element;
                        }}
                        onClick={() => {
                          setSelectedId(String(item.id));
                          setListIndex(index);
                        }}
                        onDoubleClick={() => openWorkOrder(item)}
                        style={{
                          backgroundColor: background,
                          cursor: "pointer",
                        }}
                      >
                        <td style={tdStyle} title={item.offer_number || item.offer_id}>
                          <b>{item.offer_number || item.offer_id}</b>
                        </td>

                        <td style={tdStyle} title={item.organisation_name || "-"}>
                          {item.organisation_name || "-"}
                        </td>

                        <td
                          style={tdStyle}
                          title={`${nlDate(item.execution_date) || "-"} ${item.start_time || ""}`}
                        >
                          {nlDate(item.execution_date) || "-"}
                          {item.start_time ? ` ${item.start_time}` : ""}
                        </td>

                        <td style={tdStyle} title={item.executed_by || "-"}>
                          {item.executed_by || "-"}
                        </td>

                        <td style={tdStyle}>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "3px 7px",
                              borderRadius: 999,
                              background: badge.background,
                              color: badge.color,
                              border: `1px solid ${badge.border}`,
                              whiteSpace: "nowrap",
                              fontWeight: 700,
                            }}
                          >
                            {completionLabel(item.completion_status)}
                          </span>
                        </td>

                        <td
                          style={tdStyle}
                          title={approvalLabel(item.approval_status) || "-"}
                        >
                          {approvalLabel(item.approval_status) || "-"}
                        </td>

                        <td
                          style={tdStyle}
                          title={nlDateTime(item.approval_approved_at) || "-"}
                        >
                          {nlDateTime(item.approval_approved_at) || "-"}
                        </td>

                        <td
                          style={tdStyle}
                          title={item.approval_name || item.approved_name || "-"}
                        >
                          {item.approval_name || item.approved_name || "-"}
                        </td>

                        <td
                          style={tdStyle}
                          title={item.company_code_name || item.company_code || "-"}
                        >
                          {item.company_code_name || item.company_code || "-"}
                        </td>

                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          {item.document_id ? (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                openDocument(item);
                              }}
                              style={{
                                border: "none",
                                background: "transparent",
                                padding: 0,
                                margin: 0,
                                color: "#1f4e79",
                                textDecoration: "underline",
                                cursor: "pointer",
                                font: "inherit",
                              }}
                            >
                              PDF
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {!sortedItems.length && (
                    <tr>
                      <td
                        colSpan="10"
                        style={{
                          padding: 12,
                          opacity: 0.7,
                        }}
                      >
                        Geen resultaten (filters te streng?)
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          marginTop: 14,
          padding: 14,
          background: "white",
          border: "1px solid #dce2e8",
          borderRadius: 8,
          minHeight: 92,
        }}
      >
        {selected ? (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              alignItems: "start",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 17,
                  fontWeight: 700,
                  marginBottom: 4,
                }}
              >
                {selected.offer_number || selected.offer_id} —{" "}
                {selected.organisation_name}
              </div>

              <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                Uitvoering: {nlDate(selected.execution_date) || "-"}{" "}
                {selected.start_time || ""}
                <br />
                Uitgevoerd door: {selected.executed_by || "-"}
                <br />
                Processtatus: {processStatusLabel(selected)}
                <br />
                Akkoord: {approvalLabel(selected.approval_status) || "-"}
                {selected.approval_name
                  ? ` door ${selected.approval_name}`
                  : ""}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={() => openOrder(selected)}
                style={buttonStyle}
              >
                Open opdracht
              </button>

              <button
                type="button"
                onClick={() => openWorkOrder(selected)}
                style={buttonStyle}
              >
                Open werkbon
              </button>

              <button
                type="button"
                onClick={() => openDocument(selected)}
                disabled={!selected.document_id}
                style={
                  selected.document_id
                    ? buttonStyle
                    : buttonDisabledStyle
                }
              >
                Open PDF
              </button>
            </div>
          </div>
        ) : (
          <div style={{ color: "#777" }}>
            Selecteer een werkbon voor de details en knoppen.
          </div>
        )}
      </div>
    </div>
  );
}
